import type { GoogleMapProps } from '@react-google-maps/api'
import { GoogleMap, OverlayView, useJsApiLoader } from '@react-google-maps/api'
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { useMapApiKey } from 'src/shared/api/google-maps/use-map-api-key'
import { cn } from 'src/shared/utils/cn'

import BusinessMapMarker from './business-map-marker'

type BusinessMapViewProps = {
  latitude: number
  longitude: number
  markers?: {
    lat: number
    lng: number
    name?: string
    size?: number
  }[]
  className?: string
} & GoogleMapProps

export type BusinessMapViewRef = {
  setCenter: (latitude: number, longitude: number) => void
  mapLoaded: boolean
}

const BusinessMap = forwardRef<BusinessMapViewRef, BusinessMapViewProps>(
  ({ latitude, longitude, markers, className, ...props }, ref) => {
    const { data: mapApiKey } = useMapApiKey()
    const { isLoaded } = useJsApiLoader({
      googleMapsApiKey: mapApiKey,
      libraries: ['places'],
    })

    const mapViewRef = useRef<google.maps.Map>(null)
    const [mapLoaded, setMapLoaded] = useState<boolean>(false)

    // Memoize the setCenter function to prevent unnecessary re-renders
    const setCenter = useCallback((newLatitude: number, newLongitude: number) => {
      if (mapViewRef.current) {
        mapViewRef.current.setCenter({
          lat: newLatitude,
          lng: newLongitude,
        })
      }
    }, [])

    const onMapLoaded = useCallback((map: google.maps.Map) => {
      setMapLoaded(true)
      mapViewRef.current = map
    }, [])

    useImperativeHandle(
      ref,
      () => ({
        setCenter,
        mapLoaded: mapLoaded,
      }),
      [setCenter, mapLoaded],
    )

    const memoizedMarkers = useMemo(() => {
      return markers?.map((marker) => (
        <OverlayView
          key={`${marker.lat}-${marker.lng}`}
          position={{ lat: marker.lat, lng: marker.lng }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div style={{ transform: 'translate(-50%, -100%)', pointerEvents: 'none' }}>
            <BusinessMapMarker name={marker.name} size={marker.size} />
          </div>
        </OverlayView>
      ))
    }, [markers])

    if (!isLoaded) {
      return <div className={cn('bg-accent', className)} style={{ width: '100%', height: '100%' }} />
    }

    return (
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        mapContainerClassName={className}
        center={{ lat: latitude, lng: longitude }}
        zoom={11}
        onLoad={onMapLoaded}
        options={{
          disableDefaultUI: true,
          cameraControl: false,
          zoomControl: false,
          gestureHandling: 'none',
          keyboardShortcuts: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          mapId: '6df6d8c2491ef38d15ac8174',
          rotateControl: false,
          scaleControl: false,
        }}
        {...props}
      >
        {memoizedMarkers}
      </GoogleMap>
    )
  },
)

BusinessMap.displayName = 'BusinessMap'

export default BusinessMap
